import { z } from "zod";

/**
 * Support access: an outside agent (Codvero Lab) reads and, when allowed, acts on this platform's projects and
 * chats through /api/support/v1 with a support key. The key alone opens nothing: the owner unlocks it for a while
 * with a code from their authenticator app, at a level (read, or read and act) and optionally for one project.
 */
export const SupportLevel = z.enum(["read", "act"]);
export type SupportLevel = z.infer<typeof SupportLevel>;

export interface SupportKey {
  id: string;
  name: string;
  /** the key's first characters, to tell keys apart (the key itself is shown once, when made) */
  prefix: string;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

export interface SupportGrant {
  id: string;
  keyId: string;
  keyName: string;
  level: SupportLevel;
  projectId: string | null;
  projectName: string | null;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
}

export interface SupportActivity {
  id: number;
  at: string;
  action: string;
  keyName: string | null;
  projectName: string | null;
  detail: string;
}

export interface SupportOverview {
  /** support access needs two-step sign-in on the owner's account (the unlock code comes from it) */
  totpEnabled: boolean;
  defaultMinutes: number;
  keys: SupportKey[];
  active: SupportGrant[];
  activity: SupportActivity[];
}

export const CreateSupportKeyInput = z.object({ name: z.string().trim().min(1).max(60) });
export const UnlockSupportInput = z.object({
  keyId: z.string().min(1),
  level: SupportLevel,
  projectId: z.string().nullable().default(null),
  minutes: z.number().int().min(5).max(480),
  code: z.string().regex(/^\d{6}$/),
});
export type UnlockSupportInput = z.infer<typeof UnlockSupportInput>;
export const SupportSettingsInput = z.object({ defaultMinutes: z.number().int().min(5).max(480) });
